import { createLogger } from '../../utils/logger';
import {
  SecureStorage,
  SecureStorageKey,
  SecureStorageKeys,
} from '../storage/SecureStorage';

const logger = createLogger('rateLimiter');

/**
 * Client-side rate limiting for authentication screens
 * (SECURITY_AUDIT.md §3.5). Paired with whatever the backend enforces —
 * server-side is the real gate; this is a UX + DoS-mitigation layer that
 * stops the client from even firing the request during a lockout window
 * and surfaces a clear countdown to the user.
 *
 * Persisted in the keychain (not AsyncStorage) so that clearing app data
 * on Android does not reset a lockout.
 */

export type RateLimitCategory = 'login' | 'totp';

export interface RateLimitState {
  count: number;
  lastFailureAt: number;
}

export interface RateLimitStatus {
  allowed: boolean;
  count: number;
  /** Remaining lockout in milliseconds; 0 when allowed. */
  remainingMs: number;
  /** The lockout duration this count would trigger (for messaging). */
  lockoutMs: number;
}

// Failure-count → lockout duration (ms). After the 10th failure the
// lockout is 1 hour; the audit's "disabled, requires account recovery"
// wording maps to "locked long enough to matter" here — we don't have an
// account-recovery flow wired yet, so a long client-side wait is the
// pragmatic stop.
const ONE_HOUR = 60 * 60 * 1000;
const FIVE_MIN = 5 * 60 * 1000;
const ONE_MIN = 60 * 1000;
const TEN_SEC = 10 * 1000;

export function lockoutMsForCount(count: number): number {
  if (count >= 10) return ONE_HOUR;
  if (count >= 8) return FIVE_MIN;
  if (count >= 5) return ONE_MIN;
  if (count >= 3) return TEN_SEC;
  return 0;
}

function serviceFor(category: RateLimitCategory): SecureStorageKey {
  switch (category) {
    case 'login':
      return SecureStorageKeys.rateLimitLogin;
    case 'totp':
      return SecureStorageKeys.rateLimitTotp;
    default: {
      // Exhaustiveness guard.
      const _: never = category;
      throw new Error(`unknown rate-limit category: ${String(_)}`);
    }
  }
}

async function readState(
  category: RateLimitCategory,
): Promise<RateLimitState> {
  const raw = await SecureStorage.getItem(serviceFor(category));
  if (!raw) return { count: 0, lastFailureAt: 0 };
  try {
    const parsed = JSON.parse(raw);
    if (
      typeof parsed?.count !== 'number' ||
      typeof parsed?.lastFailureAt !== 'number' ||
      parsed.count < 0 ||
      parsed.lastFailureAt < 0
    ) {
      logger.warn(`Corrupt rate-limit state for ${category}, resetting`);
      return { count: 0, lastFailureAt: 0 };
    }
    return { count: parsed.count, lastFailureAt: parsed.lastFailureAt };
  } catch {
    return { count: 0, lastFailureAt: 0 };
  }
}

async function writeState(
  category: RateLimitCategory,
  state: RateLimitState,
): Promise<void> {
  await SecureStorage.setItem(
    serviceFor(category),
    JSON.stringify(state),
    'medium',
  );
}

/**
 * Evaluate whether an attempt is currently allowed. Does not mutate
 * state; safe to call for rendering / pre-flight checks.
 */
export async function checkRateLimit(
  category: RateLimitCategory,
  now: number = Date.now(),
): Promise<RateLimitStatus> {
  const state = await readState(category);
  const lockout = lockoutMsForCount(state.count);
  if (lockout === 0) {
    return { allowed: true, count: state.count, remainingMs: 0, lockoutMs: 0 };
  }
  const elapsed = now - state.lastFailureAt;
  const remaining = lockout - elapsed;
  if (remaining <= 0) {
    return {
      allowed: true,
      count: state.count,
      remainingMs: 0,
      lockoutMs: lockout,
    };
  }
  return {
    allowed: false,
    count: state.count,
    remainingMs: remaining,
    lockoutMs: lockout,
  };
}

/**
 * Record a failed attempt. Returns the new status (post-increment) so
 * callers can immediately render the "too many attempts" banner if this
 * pushed them over a threshold.
 */
export async function recordFailure(
  category: RateLimitCategory,
  now: number = Date.now(),
): Promise<RateLimitStatus> {
  const state = await readState(category);
  const next: RateLimitState = {
    count: state.count + 1,
    lastFailureAt: now,
  };
  await writeState(category, next);
  const lockout = lockoutMsForCount(next.count);
  return {
    allowed: lockout === 0,
    count: next.count,
    remainingMs: lockout,
    lockoutMs: lockout,
  };
}

/**
 * Clear the counter for a category. Call on successful auth so the user
 * doesn't carry a previous-day typo into a future lockout.
 */
export async function recordSuccess(
  category: RateLimitCategory,
): Promise<void> {
  await SecureStorage.removeItem(serviceFor(category));
}

/**
 * Human-readable duration for the countdown banner. Always rounds UP so
 * "1s" doesn't flicker to "0s" before the lockout actually ends.
 */
export function formatRemaining(ms: number): string {
  if (ms <= 0) return '0s';
  const totalSec = Math.ceil(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (sec === 0) return `${min}m`;
  return `${min}m ${sec}s`;
}

/**
 * Test-only: clear both counters regardless of category.
 */
export async function __resetAllRateLimitsForTests(): Promise<void> {
  await SecureStorage.removeItem(SecureStorageKeys.rateLimitLogin);
  await SecureStorage.removeItem(SecureStorageKeys.rateLimitTotp);
}
